import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { CheckCircle, X, Download, Eye, AlertCircle, FileText, Upload } from 'lucide-react';
import { downloadFile } from '../utils/fileUtils';

const ApprovalDashboard = () => {
  const navigate = useNavigate();
  const [prs, setPrs] = useState([]);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'history'
  const [selectedPr, setSelectedPr] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [vendorDetails, setVendorDetails] = useState(null);
  
  // Pagination & Filter states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [costCenterFilter, setCostCenterFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  const fetchPendingApprovals = async () => {
    try {
      const response = await axios.get(`${API}/purchase-requests`, {
        headers: getAuthHeader()
      });
      setPrs(response.data);
    } catch (error) {
      toast.error('Failed to fetch purchase requests');
    }
  };

  // Helper function to preserve file extension from S3 key
  const getFilenameWithExtension = (s3Key, defaultName) => {
    if (!s3Key) return defaultName;
    
    // Extract original filename from S3 key (e.g., "proforma/uuid_invoice.png" -> "invoice.png")
    const parts = s3Key.split('/');
    const filename = parts[parts.length - 1];
    
    // Extract extension from original filename
    const extensionMatch = filename.match(/\.[^.]+$/);
    const extension = extensionMatch ? extensionMatch[0] : '.pdf';
    
    // Return default name with correct extension
    return defaultName.replace(/\.[^.]*$/, '') + extension;
  };

  // Helper function to get advance payment status badge
  const getAdvanceStatusBadge = (pr) => {
    if (!pr.is_advance_payment) return null;
    
    // Don't show badge if status is none or undefined
    if (!pr.advance_payment_status || pr.advance_payment_status === 'none') return null;
    
    const statusConfig = {
      'pending_ppa': { label: 'Pending PPA', color: 'bg-yellow-100 text-yellow-800' },
      'approved_pp': { label: 'Approved PP', color: 'bg-blue-100 text-blue-800' },
      'ppa_approved': { label: 'PP Approved', color: 'bg-green-100 text-green-800' },
      'ppa_rejected': { label: 'PP Rejected', color: 'bg-red-100 text-red-800' },
      'advance_paid': { label: 'Advance Paid', color: 'bg-emerald-100 text-emerald-800' },
      'paid': { label: 'Advance Paid', color: 'bg-emerald-100 text-emerald-800' }
    };
    
    const config = statusConfig[pr.advance_payment_status];
    if (!config) return null;
    
    return (
      <Badge className={`${config.color} text-xs w-auto inline-flex`}>
        {config.label}
      </Badge>
    );
  };

  // Get unique cost centers for filter dropdown
  const getUniqueCostCenters = () => {
    const costCenters = new Set();
    prs.forEach(pr => {
      if (pr.cost_centre) {
        costCenters.add(pr.cost_centre);
      }
    });
    return Array.from(costCenters).sort();
  };

  const uniqueCostCenters = getUniqueCostCenters();

  // Get unique vendors for filter dropdown
  const getUniqueVendors = () => {
    const vendors = new Set();
    prs.forEach(pr => {
      if (pr.vendor_name) {
        vendors.add(pr.vendor_name);
      }
    });
    return Array.from(vendors).sort();
  };

  const uniqueVendors = getUniqueVendors();

  // Filter PRs based on active/history tab
  const getFilteredPrs = () => {
    const userEmail = JSON.parse(localStorage.getItem('user')).email;
    let filtered = [];
    
    if (activeTab === 'active') {
      // Active: Pending approval (not yet acted upon by any approver)
      filtered = prs.filter(pr => pr.status === 'pending_approval');
    } else {
      // History: Any PR where this approver has acted (approved or rejected)
      filtered = prs.filter(pr => 
        (pr.approved_by === userEmail && pr.status !== 'pending_approval') || 
        (pr.status === 'rejected' && pr.rejected_by === userEmail)
      );
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(pr => 
        pr.requested_by?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.cost_centre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.reason?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply cost center filter
    if (costCenterFilter !== 'all') {
      filtered = filtered.filter(pr => pr.cost_centre === costCenterFilter);
    }

    // Apply vendor filter
    if (vendorFilter !== 'all') {
      filtered = filtered.filter(pr => pr.vendor_name === vendorFilter);
    }

    return filtered;
  };

  const filteredPrs = getFilteredPrs();
  const totalPages = Math.ceil(filteredPrs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPrs = filteredPrs.slice(startIndex, endIndex);

  // Reset to page 1 when filters change or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab, costCenterFilter, vendorFilter]);

  const handleAction = async () => {
    if (actionType === 'reject' && !rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/purchase-requests/${selectedPr.id}/approve`,
        {
          action: actionType,
          reason: actionType === 'reject' ? rejectionReason : null
        },
        { headers: getAuthHeader() }
      );

      toast.success(
        actionType === 'approve'
          ? 'Purchase request approved and PO generated!'
          : 'Purchase request rejected'
      );
      setDialogOpen(false);
      setRejectionReason('');
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const openActionDialog = (pr, action) => {
    setSelectedPr(pr);
    setActionType(action);
    setDialogOpen(true);
  };

  const openViewDialog = async (pr) => {
    setSelectedPr(pr);
    setViewDialogOpen(true);
    
    // Reset vendor details to prevent stale data
    setVendorDetails(null);
    
    // Fetch vendor details if vendor_id exists
    if (pr.vendor_id) {
      try {
        const response = await axios.get(`${API}/vendors/${pr.vendor_id}`, {
          headers: getAuthHeader()
        });
        setVendorDetails(response.data);
      } catch (error) {
        console.error('Failed to fetch vendor details:', error);
        setVendorDetails(null);
      }
    }
  };

  const getStatusBadge = (status, holdStatus, pr) => {
    let badgeClass = '';
    let statusText = '';
    
    switch (status) {
      case 'pending_approval':
        badgeClass = 'bg-yellow-100 text-yellow-800';
        statusText = 'Pending';
        break;
      case 'approved':
      case 'finance_approved':
      case 'po_generated':
        badgeClass = 'bg-green-100 text-green-800';
        statusText = 'Approved';
        break;
      case 'rejected':
        badgeClass = 'bg-red-100 text-red-800';
        statusText = 'Rejected';
        break;
      case 'additional_info_requested':
        badgeClass = 'bg-orange-100 text-orange-800';
        statusText = 'Info Requested';
        break;
      case 'paid':
        badgeClass = 'bg-blue-100 text-blue-800';
        statusText = 'Paid';
        break;
      case 'payment_in_progress':
        badgeClass = 'bg-indigo-100 text-indigo-800';
        statusText = 'Payment in Progress';
        break;
      default:
        badgeClass = 'bg-gray-100 text-gray-800';
        statusText = status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    
    // Return badges with request type indicator
    return (
      <div className="flex flex-col gap-1 items-start">
        {pr?.is_advance_payment && (
          <Badge className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white inline-flex items-center gap-1 px-2 py-0.5 w-auto">
            <CheckCircle className="w-3 h-3" />
            <span className="text-xs font-semibold">AR</span>
          </Badge>
        )}
        {!pr?.is_advance_payment && !pr?.is_upload_invoice && (
          <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white inline-flex items-center gap-1 px-2 py-0.5 w-auto">
            <CheckCircle className="w-3 h-3" />
            <span className="text-xs font-semibold">PR</span>
          </Badge>
        )}
        {pr?.is_upload_invoice && (
          <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white inline-flex items-center gap-1 px-2 py-0.5 w-auto">
            <Upload className="w-3 h-3" />
            <span className="text-xs font-semibold">UI</span>
          </Badge>
        )}
        <Badge className={`${badgeClass} inline-flex w-auto`}>{statusText}</Badge>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
              Pending Approvals
            </h1>
          </div>
        </div>
      </nav>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Approvals Section - Active and History Tabs */}
        {/* Tabs for Active and History */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('active')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'active'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Active Approvals ({prs.filter(pr => pr.status === 'pending_approval').length})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              History ({prs.filter(pr => {
                const userEmail = JSON.parse(localStorage.getItem('user')).email;
                return (pr.approved_by === userEmail && pr.status !== 'pending_approval') || 
                       (pr.status === 'rejected' && pr.rejected_by === userEmail);
              }).length})
            </button>
          </nav>
        </div>

        <Card data-testid="approval-list">
          <CardHeader>
            <h2 className="text-lg font-semibold text-gray-900">
              {activeTab === 'active' ? 'Active Approvals' : 'Approval History'}
            </h2>
          </CardHeader>
          <CardContent>
            {/* Filter and Search Controls */}
              <div className="mb-4 flex flex-wrap gap-4 items-center justify-between">
                <div className="flex gap-3 items-center flex-1">
                  <input
                    type="text"
                    placeholder="Search by requester, vendor, entity, cost center, or reason..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 flex-1 max-w-md"
                  />
                  <select
                    value={vendorFilter}
                    onChange={(e) => setVendorFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all">All Vendors</option>
                    {uniqueVendors.map(vendor => (
                      <option key={vendor} value={vendor}>{vendor}</option>
                    ))}
                  </select>
                  <select
                    value={costCenterFilter}
                    onChange={(e) => setCostCenterFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all">All Cost Centers</option>
                    {uniqueCostCenters.map(cc => (
                      <option key={cc} value={cc}>{cc}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Show:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-sm text-gray-600">per page</span>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Requested By</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Vendor Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Entity</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Cost Centre</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Reason</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                      <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">View</th>
                      {activeTab === 'active' && (
                        <>
                          <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">Approve</th>
                          <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">Reject</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                {currentPrs.length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === 'active' ? "10" : "8"} className="px-4 py-8 text-center text-gray-500">
                      No requests found
                    </td>
                  </tr>
                ) : (
                  currentPrs.map((pr) => {
                  const userEmail = JSON.parse(localStorage.getItem('user')).email;
                  const displayStatus = activeTab === 'history' 
                    ? (pr.approved_by === userEmail ? 'approved' : 'rejected')
                    : pr.status;
                  
                  return (
                    <tr key={pr.id}>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {pr.requested_by}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {pr.vendor_name || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {pr.entity_name || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {pr.cost_centre}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        ₹{(pr.total_amount || pr.base_amount || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={pr.reason}>
                        {pr.reason}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1 items-start">
                          {getStatusBadge(displayStatus, pr.hold_status, pr)}
                          {getAdvanceStatusBadge(pr)}
                          {pr.hold_status && (
                            <Badge className="bg-red-100 text-red-800 w-auto inline-flex">On Hold</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <Button
                          onClick={() => openViewDialog(pr)}
                          variant="ghost"
                          size="sm"
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                      {activeTab === 'active' && (
                        <>
                          <td className="px-3 py-3 text-center">
                            <Button
                              onClick={() => openActionDialog(pr, 'approve')}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`approve-button-${pr.id}`}
                              disabled={pr.hold_status}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <Button
                              onClick={() => openActionDialog(pr, 'reject')}
                              size="sm"
                              variant="destructive"
                              data-testid={`reject-button-${pr.id}`}
                              disabled={pr.hold_status}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {filteredPrs.length > 0 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredPrs.length)} of {filteredPrs.length} results
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                >
                  Previous
                </Button>
                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      return page === 1 || 
                             page === totalPages || 
                             (page >= currentPage - 1 && page <= currentPage + 1);
                    })
                    .map((page, index, array) => (
                      <div key={page} className="flex items-center">
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        <Button
                          onClick={() => setCurrentPage(page)}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          className={currentPage === page ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                        >
                          {page}
                        </Button>
                      </div>
                    ))
                  }
                </div>
                <Button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="sm"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

        {/* Action Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {actionType === 'approve' ? 'Approve Purchase Request' : 'Reject Purchase Request'}
              </DialogTitle>
              <DialogDescription>
                {actionType === 'approve'
                  ? 'This will generate a Purchase Order and send it to the vendor.'
                  : 'Please provide a reason for rejection.'}
              </DialogDescription>
            </DialogHeader>

            {actionType === 'reject' && (
              <div className="space-y-2 mt-4">
                <Label htmlFor="rejection_reason">Rejection Reason</Label>
                <textarea
                  id="rejection_reason"
                  data-testid="rejection-reason-input"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows={4}
                  placeholder="Enter reason for rejection..."
                  required
                />
              </div>
            )}

            <div className="flex justify-end space-x-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAction}
                className={actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                variant={actionType === 'reject' ? 'destructive' : 'default'}
                disabled={loading}
                data-testid="confirm-action-button"
              >
                {loading ? 'Processing...' : (actionType === 'approve' ? 'Approve' : 'Reject')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Details Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Purchase Request Details</DialogTitle>
            </DialogHeader>
            {selectedPr && (() => {
              // Calculate display status the same way as in the table
              const userEmail = JSON.parse(localStorage.getItem('user')).email;
              const displayStatus = activeTab === 'history' 
                ? (selectedPr.approved_by === userEmail ? 'approved' : 'rejected')
                : selectedPr.status;
              
              return (
                <div className="space-y-6">
                  {/* Status Badge - Direct display without label */}
                  <div className="flex items-center gap-2">
                    {getStatusBadge(displayStatus, selectedPr.hold_status, selectedPr)}
                    {selectedPr.hold_status && (
                      <Badge className="bg-red-100 text-red-800 w-auto inline-flex">On Hold</Badge>
                    )}
                  </div>

                {/* Hold Reason if present */}
                {selectedPr.hold_status && selectedPr.hold_reason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-red-800 mb-2">Hold Reason:</p>
                    {selectedPr.hold_reason_type && (
                      <p className="text-sm font-medium text-red-800 mb-2">Type: {selectedPr.hold_reason_type}</p>
                    )}
                    <p className="text-sm text-red-700">{selectedPr.hold_reason}</p>
                    {selectedPr.held_by && selectedPr.held_at && (
                      <p className="text-xs text-red-600 mt-2">
                        Held by: {selectedPr.held_by} on {new Date(selectedPr.held_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}

                {/* Rejection Reason if present and rejected by current user */}
                {selectedPr.status === 'rejected' && selectedPr.rejection_reason && 
                 selectedPr.rejected_by === JSON.parse(localStorage.getItem('user')).email && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-red-800 mb-2">Rejection Reason:</p>
                    <p className="text-sm text-red-700">{selectedPr.rejection_reason}</p>
                  </div>
                )}

                {/* Basic Information */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Requested By</p>
                    <p className="text-sm font-medium mt-1">{selectedPr.requested_by}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Entity</p>
                    <p className="text-sm font-medium mt-1">{selectedPr.entity_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Cost Centre</p>
                    <p className="text-sm font-medium mt-1">{selectedPr.cost_centre}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Inclusive Tax</p>
                    <p className="text-sm font-medium mt-1">{selectedPr.inclusive_tax || 'No'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Base Amount</p>
                    <p className="text-sm font-medium mt-1">₹{selectedPr.base_amount?.toLocaleString() || '0'}</p>
                  </div>
                  {selectedPr.inclusive_tax === 'Yes' && (
                    <>
                      <div>
                        <p className="text-sm text-gray-600">Tax Amount</p>
                        <p className="text-sm font-medium mt-1">₹{(selectedPr.tax_amount || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total Amount</p>
                        <p className="text-sm font-medium mt-1 text-green-700">₹{(selectedPr.total_amount || selectedPr.base_amount || 0).toLocaleString()}</p>
                      </div>
                    </>
                  )}
                  {selectedPr.po_number && (
                    <div>
                      <p className="text-sm text-gray-600">PO Number</p>
                      <p className="text-sm font-medium mt-1 text-blue-600">{selectedPr.po_number}</p>
                    </div>
                  )}
                  {/* TDS Related Fields */}
                  {selectedPr.is_tds_applicable === 'Yes' && (
                    <>
                      <div>
                        <p className="text-sm text-gray-600">TDS Applicable</p>
                        <p className="text-sm font-medium mt-1 text-orange-600">Yes</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">TDS Percentage</p>
                        <p className="text-sm font-medium mt-1">{selectedPr.tds_percentage || 0}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Payable Amount</p>
                        <p className="text-sm font-medium mt-1 text-blue-700 font-semibold">
                          ₹{(selectedPr.payable_amount || 0).toLocaleString()}
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* Reason */}
                <div>
                  <p className="text-sm text-gray-600 mb-2">Reason</p>
                  <p className="text-sm text-gray-900 p-3 bg-gray-50 rounded-lg">{selectedPr.reason}</p>
                </div>

                {/* Vendor Name Only */}
                {vendorDetails && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Vendor</p>
                    <p className="text-sm font-medium text-gray-900 p-3 bg-gray-50 rounded-lg">
                      {vendorDetails.legal_entity_name}
                    </p>
                  </div>
                )}

                {/* Advance Payment Details */}
                {selectedPr.is_advance_payment && (
                  <div className="bg-amber-50 rounded-lg p-4 space-y-3 border-2 border-amber-200">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-amber-900">Advance Payment Request</h3>
                      {getAdvanceStatusBadge(selectedPr)}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm font-medium text-amber-700">Total Amount:</span>
                        <p className="text-sm font-bold text-amber-900">₹{(selectedPr.total_amount || selectedPr.base_amount || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-amber-700">Advance Payment:</span>
                        <p className="text-sm font-bold text-green-900 bg-green-50 p-2 rounded">₹{(selectedPr.advance_amount || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-amber-700">Payment Status:</span>
                        <p className="text-sm font-medium text-amber-900">
                          {selectedPr.is_partial_paid ? 'Advance Paid' : 'Advance Pending'}
                        </p>
                      </div>
                      {selectedPr.advance_approved_by && (
                        <div className="col-span-2">
                          <span className="text-sm font-medium text-green-700">PPA Approved By:</span>
                          <p className="text-sm text-green-900">{selectedPr.advance_approved_by} on {new Date(selectedPr.advance_approved_at).toLocaleString()}</p>
                        </div>
                      )}
                      {selectedPr.advance_rejected_by && (
                        <>
                          <div className="col-span-2">
                            <span className="text-sm font-medium text-red-700">PPA Rejected By:</span>
                            <p className="text-sm text-red-900">{selectedPr.advance_rejected_by} on {new Date(selectedPr.advance_rejected_at).toLocaleString()}</p>
                          </div>
                          <div className="col-span-2">
                            <span className="text-sm font-medium text-red-700">Rejection Reason:</span>
                            <p className="text-sm text-red-900 p-2 bg-red-50 rounded">{selectedPr.advance_rejection_reason}</p>
                          </div>
                        </>
                      )}
                      {selectedPr.advance_utr_number && (
                        <div className="col-span-2">
                          <span className="text-sm font-medium text-green-700">Advance UTR:</span>
                          <p className="text-sm text-green-900">{selectedPr.advance_utr_number} (Paid on {new Date(selectedPr.advance_paid_at).toLocaleDateString()})</p>
                        </div>
                      )}
                    </div>
                    <div className="bg-amber-100 p-3 rounded mt-2">
                      <p className="text-xs text-amber-800">
                        <strong>Note:</strong> Upon approval, PO will be generated and sent to Finance Head for Partial Payment Approval (PPA). 
                        After advance is paid and delivery confirmed, remaining amount will require separate finance approval.
                      </p>
                    </div>
                  </div>
                )}

                {/* Partial Payment Info Section - Show if any partial payments made */}
                {((selectedPr.partial_payments && selectedPr.partial_payments.length > 0) || (selectedPr.total_paid_amount && selectedPr.total_paid_amount > 0)) && (
                  <div className="bg-amber-50 rounded-lg p-4 space-y-3 border-2 border-amber-200">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-amber-900">Partial Payment Info</h3>
                      <Badge className={selectedPr.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                        {selectedPr.payment_status === 'paid' ? 'Fully Paid' : 'Partly Paid'}
                      </Badge>
                    </div>
                    
                    {/* Balance Amount Summary */}
                    <div className="grid grid-cols-3 gap-4 bg-white p-3 rounded-lg">
                      <div>
                        <p className="text-xs text-gray-600">Payable Amount</p>
                        <p className="text-sm font-bold text-gray-900">
                          ₹{(() => {
                            let totalPayable = selectedPr.payable_amount || selectedPr.total_amount || selectedPr.base_amount || 0;
                            if (selectedPr.is_advance_payment && selectedPr.is_partial_paid) {
                              totalPayable = totalPayable - (selectedPr.advance_amount || 0);
                            }
                            return totalPayable.toLocaleString();
                          })()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Total Paid</p>
                        <p className="text-sm font-bold text-green-700">
                          ₹{(selectedPr.total_paid_amount || 0).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Balance Amount</p>
                        <p className="text-sm font-bold text-amber-700">
                          ₹{(() => {
                            let totalPayable = selectedPr.payable_amount || selectedPr.total_amount || selectedPr.base_amount || 0;
                            if (selectedPr.is_advance_payment && selectedPr.is_partial_paid) {
                              totalPayable = totalPayable - (selectedPr.advance_amount || 0);
                            }
                            return (totalPayable - (selectedPr.total_paid_amount || 0)).toLocaleString();
                          })()}
                        </p>
                      </div>
                    </div>

                    {/* Payment History */}
                    {selectedPr.partial_payments && selectedPr.partial_payments.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-amber-900">Payment History:</p>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {selectedPr.partial_payments.map((payment, index) => (
                            <div key={payment.id || index} className="bg-white p-3 rounded-lg border border-amber-200">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">
                                    Payment #{index + 1}: ₹{(payment.amount || 0).toLocaleString()}
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    UTR: {payment.utr_number} | {payment.payment_method}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-600">
                                    {payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() : 'N/A'}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    by {payment.paid_by}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Tax Invoice Details Section */}
                {selectedPr.tax_invoice_url && (selectedPr.tax_invoice_date || selectedPr.credit_period || selectedPr.due_date || selectedPr.paid_at || selectedPr.utr_number) && (
                  <div className="border-t pt-4">
                    <h3 className="text-base font-semibold text-gray-900 mb-3">Tax Invoice Details</h3>
                    <div className="bg-green-50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        {selectedPr.tax_invoice_date && (
                          <div>
                            <span className="font-medium text-gray-700">Invoice Date:</span>
                            <p className="text-gray-900 mt-1">
                              {new Date(selectedPr.tax_invoice_date).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                        {selectedPr.credit_period && (
                          <div>
                            <span className="font-medium text-gray-700">Credit Period:</span>
                            <p className="text-gray-900 mt-1">{selectedPr.credit_period} days</p>
                          </div>
                        )}
                        {selectedPr.due_date && (
                          <div>
                            <span className="font-medium text-gray-700">Due Date:</span>
                            <p className="text-gray-900 mt-1">
                              {new Date(selectedPr.due_date).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                        {selectedPr.paid_at && (
                          <div>
                            <span className="font-medium text-gray-700">Paid On:</span>
                            <p className="text-gray-900 mt-1">
                              {new Date(selectedPr.paid_at).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                        {selectedPr.utr_number && (
                          <div>
                            <span className="font-medium text-gray-700">UTR Number:</span>
                            <p className="text-gray-900 mt-1">{selectedPr.utr_number}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Documents Section */}
                {(selectedPr.proforma_invoice_url || selectedPr.tax_invoice_url || selectedPr.po_url) && (
                  <div className="border-t pt-4">
                    <h3 className="text-base font-semibold text-gray-900 mb-3">Documents</h3>
                    <div className="space-y-2">
                      {selectedPr.proforma_invoice_url && (
                        <Button
                          onClick={() => downloadFile(
                            selectedPr.proforma_invoice_url, 
                            getFilenameWithExtension(selectedPr.proforma_invoice_url, `Proforma_Invoice_${selectedPr.id}.pdf`)
                          )}
                          variant="outline"
                          size="sm"
                          className="w-full justify-start text-indigo-600 hover:text-indigo-700"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Proforma Invoice
                        </Button>
                      )}
                      {selectedPr.tax_invoice_url && (
                        <Button
                          onClick={() => downloadFile(
                            selectedPr.tax_invoice_url, 
                            getFilenameWithExtension(selectedPr.tax_invoice_url, `Tax_Invoice_${selectedPr.id}.pdf`)
                          )}
                          variant="outline"
                          size="sm"
                          className="w-full justify-start text-indigo-600 hover:text-indigo-700"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Tax Invoice
                        </Button>
                      )}
                      {selectedPr.po_url && (
                        <Button
                          onClick={() => downloadFile(selectedPr.po_url, `PO_${selectedPr.po_number}.pdf`)}
                          variant="outline"
                          size="sm"
                          className="w-full justify-start text-indigo-600 hover:text-indigo-700"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Purchase Order
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Document Trail */}
                <div className="border-t pt-4">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">Document Trail</h3>
                  <div className="space-y-2">
                    {/* Purchase Request Created */}
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Purchase Request Created</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.requested_by} on {new Date(selectedPr.created_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                    
                    {/* Proforma Invoice Uploaded */}
                    {selectedPr.proforma_invoice_url && (
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">Proforma Invoice Uploaded</div>
                          <div className="text-xs text-gray-500">
                            by {selectedPr.requested_by} on {new Date(selectedPr.created_at).toLocaleDateString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric', 
                              hour: '2-digit', minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Function Approved */}
                    {selectedPr.approved_by && selectedPr.approved_at && (
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">Function Approved</div>
                          <div className="text-xs text-gray-500">
                            by {selectedPr.approved_by} on {new Date(selectedPr.approved_at).toLocaleDateString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric', 
                              hour: '2-digit', minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* PO Generated */}
                    {selectedPr.po_number && (
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">PO Generated</div>
                          <div className="text-xs text-gray-500">
                            PO #{selectedPr.po_number}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Delivery Confirmed */}
                    {selectedPr.delivery_confirmed && selectedPr.delivery_confirmed_at && (
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">Delivery Confirmed</div>
                          <div className="text-xs text-gray-500">
                            by {selectedPr.requested_by} on {new Date(selectedPr.delivery_confirmed_at).toLocaleDateString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric', 
                              hour: '2-digit', minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tax Invoice Uploaded */}
                    {selectedPr.tax_invoice_url && selectedPr.tax_invoice_date && (
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">Tax Invoice Uploaded</div>
                          <div className="text-xs text-gray-500">
                            by {selectedPr.requested_by} on {new Date(selectedPr.tax_invoice_date).toLocaleDateString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric', 
                              hour: '2-digit', minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Finance Approved */}
                    {selectedPr.finance_approved_by && selectedPr.finance_approved_at && (
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">Finance Approved</div>
                          <div className="text-xs text-gray-500">
                            by {selectedPr.finance_approved_by} on {new Date(selectedPr.finance_approved_at).toLocaleDateString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric', 
                              hour: '2-digit', minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Payment Completed */}
                    {selectedPr.paid_at && (
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">Payment Completed</div>
                          <div className="text-xs text-gray-500">
                            by {selectedPr.paid_by} on {new Date(selectedPr.paid_at).toLocaleDateString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric', 
                              hour: '2-digit', minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Metadata */}
                <div className="border-t pt-4 text-xs text-gray-500 space-y-1">
                  {selectedPr.created_at && (
                    <p>Created: {new Date(selectedPr.created_at).toLocaleString()}</p>
                  )}
                  {selectedPr.approved_at && (
                    <p>Approved: {new Date(selectedPr.approved_at).toLocaleString()}</p>
                  )}
                  {selectedPr.rejected_at && (
                    <p>Rejected: {new Date(selectedPr.rejected_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
            );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ApprovalDashboard;
